import React, { useState } from "react";
import { Drawer, Form, Select, DatePicker, Descriptions } from "antd";
import { useIsMobile, useIsTablet } from "../../Hook";
import dayjs from "dayjs";
import { PaymentOption } from "../PaymentsOption";
import { drawerFooter, drawerTitle } from "../AppUi";

interface Props {
  open: boolean;
  onClose: () => void;
  creditPaymentOrder: any;
  paymentMethods: any[];
  bankAccounts: any[];
  createRepaymentMutation: any;
  handleSaveCreditPayment: (values: any) => void;
}

export const CreditPaymentDrawer: React.FC<Props> = ({
  open,
  onClose,
  creditPaymentOrder,
  paymentMethods,
  bankAccounts,
  createRepaymentMutation,
  handleSaveCreditPayment,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [creditPaymentForm] = Form.useForm();

  const [paymentMethodCode, setPaymentMethodCode] = useState("");
  const [repayAmount, setRepayAmount] = useState(0);

  const [paymentDetails, setPaymentDetails] = useState({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });

  return (
    <Drawer
      title={drawerTitle("Record Invoice Credit Payment")}
      width={isMobile || isTablet ? "100%" : 560}
      open={open}
      destroyOnClose
      onClose={() => {
        creditPaymentForm.resetFields();
        setPaymentMethodCode("");
        setRepayAmount(0);
        setPaymentDetails({
          amount: 0,
          note: "",
          bank_account_id: "",
          transaction_no: "",
          reference_no: "",
          cheques: [],
        });

        onClose();
      }}
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: onClose },
          {
            label: "Submit Payment",
            type: "primary",
            loading: createRepaymentMutation.isPending,
            disabled:
              repayAmount <= 0 ||
              repayAmount > (creditPaymentOrder?.due_amount || 0) + 0.001,
            onClick: () => creditPaymentForm.submit(),
          },
        ],
      })}
    >
      {creditPaymentOrder && (
        <div className="flex flex-col gap-4">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <Descriptions size="small" column={2}>
              <Descriptions.Item
                label={<span className="text-gray-500">Invoice No</span>}
              >
                <span className="font-bold">{creditPaymentOrder.order_no}</span>
              </Descriptions.Item>

              <Descriptions.Item
                label={<span className="text-gray-500">Customer</span>}
              >
                <span className="font-bold">
                  {creditPaymentOrder.customer_name || "Walk-in"}
                </span>
              </Descriptions.Item>

              <Descriptions.Item
                label={<span className="text-gray-500">Grand Total</span>}
              >
                LKR {creditPaymentOrder.grand_total.toLocaleString()}
              </Descriptions.Item>

              <Descriptions.Item
                label={<span className="text-gray-500">Amount Paid</span>}
              >
                <span className="text-emerald-600 font-semibold">
                  LKR {creditPaymentOrder.paid_amount.toLocaleString()}
                </span>
              </Descriptions.Item>

              <Descriptions.Item
                span={2}
                label={<span className="text-gray-500">Outstanding Due</span>}
              >
                <span className="font-bold text-red-500">
                  LKR {creditPaymentOrder.due_amount.toLocaleString()}
                </span>
              </Descriptions.Item>
            </Descriptions>
          </div>

          <Form
            form={creditPaymentForm}
            layout="vertical"
            initialValues={{
              transaction_date: dayjs(),
            }}
            onFinish={(values) => {
              handleSaveCreditPayment({
                ...values,
                paymentDetails,
              });
            }}
          >
            <Form.Item
              name="payment_method_id"
              label="Payment Method"
              rules={[
                {
                  required: true,
                  message: "Please select payment method",
                },
              ]}
            >
              <Select
                placeholder="Select payment method"
                onChange={(val) => {
                  const pm = paymentMethods.find((x) => x.id === val);

                  setPaymentMethodCode(pm?.code || "");

                  setPaymentDetails({
                    amount: 0,
                    note: "",
                    bank_account_id: "",
                    transaction_no: "",
                    reference_no: "",
                    cheques: [],
                  });

                  setRepayAmount(0);
                }}
              >
                {paymentMethods.map((pm) => (
                  <Select.Option key={pm.id} value={pm.id}>
                    {pm.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {paymentMethodCode && (
              <div className="mb-5 border-t pt-4">
                <PaymentOption
                  paymentMethodCode={paymentMethodCode}
                  value={paymentDetails}
                  bankAccounts={bankAccounts}
                  onChange={(val: any) => {
                    setPaymentDetails(val);
                    setRepayAmount(val.amount || 0);
                  }}
                />
              </div>
            )}

            <Form.Item
              name="transaction_date"
              label="Payment Date"
              rules={[
                {
                  required: true,
                  message: "Select payment date",
                },
              ]}
            >
              <DatePicker
                className="w-full"
                showTime
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>

            {repayAmount > 0 && (
              <div className="mt-4 rounded-lg border border-emerald-100 bg-emerald-50 p-3 flex justify-between">
                <span>Remaining Due</span>

                <span className="font-bold">
                  LKR{" "}
                  {Math.max(
                    0,
                    creditPaymentOrder.due_amount - repayAmount,
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </Form>
        </div>
      )}
    </Drawer>
  );
};
